import React, { useState } from 'react';
import { ToggleRight, UserX, UserCheck, Users, Search, Crown, Star, User, Shield, AlertCircle, CheckCircle, Clock, Zap } from 'lucide-react';

const mockUsers = [
  { id: 1, username: 'admin', role: 'admin', is_active: true, email: 'admin@bas.gov.la', last_login: '2024-03-15 14:30', created_at: '2023-01-15', login_attempts: 0 },
  { id: 2, username: 'staff01', role: 'staff', is_active: true, email: 'staff01@bas.gov.la', last_login: '2024-03-15 09:15', created_at: '2023-02-20', login_attempts: 0 },
  { id: 3, username: 'cust001', role: 'customer', is_active: false, email: 'customer@example.com', last_login: '2024-03-10 16:45', created_at: '2023-05-10', login_attempts: 3 },
  { id: 4, username: 'staff02', role: 'staff', is_active: true, email: 'staff02@bas.gov.la', last_login: '2024-03-14 11:20', created_at: '2023-03-01', login_attempts: 1 },
  { id: 5, username: 'viewer01', role: 'customer', is_active: true, email: 'viewer@example.com', last_login: '2024-03-15 08:30', created_at: '2023-06-15', login_attempts: 0 },
];

const ManagementAccountStatus = () => {
  const [users, setUsers] = useState(mockUsers);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');

  const toggle = (id) => {
    setUsers(prev => prev.map(u => u.id === id ? { ...u, is_active: !u.is_active } : u));
  };

  const getRoleIcon = (role) => {
    switch(role.toLowerCase()) {
      case 'admin': return <Crown className="h-4 w-4 text-red-600" />;
      case 'staff': return <Star className="h-4 w-4 text-blue-600" />;
      case 'customer': return <User className="h-4 w-4 text-green-600" />;
      default: return <User className="h-4 w-4 text-gray-600" />;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch(role.toLowerCase()) {
      case 'admin': return 'bg-red-100 text-red-800 border-red-200';
      case 'staff': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'customer': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = searchTerm === '' || 
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && user.is_active) ||
      (statusFilter === 'inactive' && !user.is_active);
    
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    
    return matchesSearch && matchesStatus && matchesRole;
  });

  return (
    <main className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <ToggleRight className="h-8 w-8 mr-3 text-blue-600" />
              Account Status Control
            </h1>
            <p className="text-gray-600 mt-2">Manage user account access and monitor account health</p>
          </div>
          <div className="hidden md:flex items-center space-x-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{users.filter(u => u.is_active).length}</div>
              <div className="text-sm text-gray-500">Active</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{users.filter(u => !u.is_active).length}</div>
              <div className="text-sm text-gray-500">Inactive</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">{users.filter(u => u.login_attempts > 2).length}</div>
              <div className="text-sm text-gray-500">Locked</div>
            </div>
          </div>
        </div>

        {/* Quick Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {[
            { title: 'Total Accounts', value: users.length, icon: <Users className="h-6 w-6" />, color: 'bg-blue-500', textColor: 'text-blue-600' },
            { title: 'Active Users', value: users.filter(u => u.is_active).length, icon: <CheckCircle className="h-6 w-6" />, color: 'bg-green-500', textColor: 'text-green-600' },
            { title: 'Suspended', value: users.filter(u => !u.is_active).length, icon: <AlertCircle className="h-6 w-6" />, color: 'bg-red-500', textColor: 'text-red-600' },
            { title: 'Failed Logins', value: users.reduce((sum, u) => sum + u.login_attempts, 0), icon: <Shield className="h-6 w-6" />, color: 'bg-yellow-500', textColor: 'text-yellow-600' }
          ].map((stat, index) => (
            <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">{stat.title}</p>
                  <p className={`text-3xl font-bold ${stat.textColor} mt-1`}>{stat.value}</p>
                </div>
                <div className={`p-3 rounded-lg ${stat.color} text-white`}>
                  {stat.icon}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 mb-6">
          <div className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent" 
                    placeholder="Search users by username or email..."
                  />
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <select 
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active Only</option>
                  <option value="inactive">Inactive Only</option>
                </select>
                <select 
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="all">All Roles</option>
                  <option value="admin">Admin</option>
                  <option value="staff">Staff</option>
                  <option value="customer">Customer</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Shield className="h-5 w-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">Account Management</h2>
              </div>
              <div className="text-sm text-gray-500">
                {filteredUsers.length} accounts found
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Account</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role & Permissions</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Activity</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account Status</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map(u => (
                  <tr key={u.id} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-r from-orange-400 to-red-500 flex items-center justify-center text-white font-medium">
                          {u.username.charAt(0).toUpperCase()}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{u.username}</div>
                          <div className="text-sm text-gray-500">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(u.role)}`}>
                        {getRoleIcon(u.role)}
                        <span className="ml-1 capitalize">{u.role}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">Since {u.created_at}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center text-sm text-gray-900">
                        <Clock className="h-4 w-4 text-gray-400 mr-2" />
                        {getTimeAgo(u.last_login)}
                      </div>
                      {u.login_attempts > 0 && (
                        <div className="text-xs text-red-600 mt-1">
                          {u.login_attempts} failed attempts
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {u.is_active ? (
                          <div className="flex items-center">
                            <div className="w-3 h-3 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Active
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <div className="w-3 h-3 bg-red-400 rounded-full mr-2"></div>
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Suspended
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => toggle(u.id)} 
                          className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${u.is_active 
                            ? 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200' 
                            : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                          }`}
                        >
                          {u.is_active ? (
                            <>
                              <UserX className="h-4 w-4 mr-1"/> 
                              Suspend
                            </>
                          ) : (
                            <>
                              <UserCheck className="h-4 w-4 mr-1"/> 
                              Activate
                            </>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Account changes are applied immediately
              </div>
              <div className="flex items-center space-x-4">
                <button className="text-sm text-orange-600 hover:text-orange-700 font-medium flex items-center">
                  <Zap className="h-4 w-4 mr-1" />
                  Bulk Actions
                </button>
                <button className="text-sm text-orange-600 hover:text-orange-700 font-medium">
                  Export Report
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Security Notice */}
        <div className="mt-6 bg-gradient-to-r from-amber-50 to-orange-100 border border-amber-200 rounded-xl p-6">
          <div className="flex items-start">
            <Shield className="h-6 w-6 text-amber-600 mr-3 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-amber-800 mb-2">Security & Compliance Notice</h3>
              <div className="space-y-2 text-sm text-amber-700">
                <p>• Account status changes are logged in the audit trail for compliance purposes</p>
                <p>• Suspended accounts cannot access the system until manually reactivated</p>
                <p>• Failed login attempts are automatically tracked and may trigger security alerts</p>
                <p>• Administrative actions require proper authorization and are subject to review</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ManagementAccountStatus;
